import { Button, Flex, Grid } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React, { useCallback, useContext } from 'react';

import DatePickerHeader from '@components/common/date/DatePickerHeader';
import DatePickerPager from '@components/common/date/DatePickerPager';
import DatePickerTextInput from '@components/common/date/DatePickerTextInput';
import { DatePickerReactContext } from '@components/common/date/context';
import { DatePickerProps } from '@components/common/date/types';
import { shortQuarterFormat, TODAY } from 'helpers/dates';

const quarterToMonth = (quarter: number) => (quarter - 1) * 3 + 1;

const QuarterPicker: React.FC<Omit<DatePickerProps, 'hideYear'>> = ({
  onDateSelect,
  selected = TODAY,
  minDate,
  maxDate,
  focused = true,
}) => {
  return (
    <DatePickerHeader onDateSelect={onDateSelect} selected={selected} pageBy="year">
      <DatePickerTextInput
        selected={selected}
        focused={focused}
        formatter={shortQuarterFormat}
        minDate={minDate}
        maxDate={maxDate}
      />
      <Flex w="full" px="0.75rem">
        <DatePickerPager />
      </Flex>
      <QuarterPickerBody selected={selected} minDate={minDate} maxDate={maxDate} />
    </DatePickerHeader>
  );
};

interface QuarterPickerBodyProps {
  minDate: DateTime;
  maxDate: DateTime;
  selected?: DateTime;
}

const QuarterPickerBody: React.FC<QuarterPickerBodyProps> = ({ selected, minDate, maxDate }) => {
  const { pagedYear, onDateSelect } = useContext(DatePickerReactContext);

  const isQuarterDisabled = (quarter: number) => {
    const month = quarterToMonth(quarter);
    const startOfMonth = DateTime.utc(pagedYear, month);
    return startOfMonth < minDate || startOfMonth > maxDate;
  };

  const onQuarterSelect = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      ev.stopPropagation();
      const month = quarterToMonth(Number(ev.currentTarget.value));
      const date = DateTime.utc(pagedYear, month);
      onDateSelect(date, { reason: 'button' });
    },
    [onDateSelect, pagedYear],
  );

  return (
    <Grid flex="1 0" templateColumns="repeat(4, 1fr)" columnGap={2} w="16rem" h="7.5rem">
      {[1, 2, 3, 4].map((quarter) => {
        const isSelected =
          selected != null && selected.quarter === quarter && selected.year === pagedYear;
        return (
          <Button
            key={quarter}
            data-selected={isSelected || undefined}
            data-testid={isSelected ? 'selected-quarter' : undefined}
            disabled={isQuarterDisabled(quarter)}
            variant={isSelected ? 'subtle' : 'plain'}
            _selected={{ bg: 'surface.accent' }}
            size="sm"
            py="0.375rem"
            onClick={onQuarterSelect}
            value={quarter}
          >
            Q{quarter}
          </Button>
        );
      })}
    </Grid>
  );
};

export default React.memo(QuarterPicker);
